import { useEffect, useState } from 'react';

import { type AdminLookupResult, adminClient } from '@/api/adminClient';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

const MIN_QUERY_LENGTH = 2;

export type AdminLookupState = {
  /** The query the results below answer. */
  query: string;
  results: AdminLookupResult[];
  loading: boolean;
  error?: string;
};

/** Studio Admin search for `query`, once typing pauses; an older answer never replaces a newer one. */
export function useAdminLookup(query: string, delayMs = 200): AdminLookupState {
  const debounced = useDebouncedValue(query.trim(), delayMs);
  const [state, setState] = useState<AdminLookupState>({ query: '', results: [], loading: false });

  useEffect(() => {
    if (debounced.length < MIN_QUERY_LENGTH) {
      setState({ query: debounced, results: [], loading: false });
      return undefined;
    }
    const controller = new AbortController();
    setState((previous) => ({ ...previous, loading: true, error: undefined }));
    adminClient
      .lookup(debounced, controller.signal)
      .then((response) => setState({ query: debounced, results: response.results, loading: false }))
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        setState({ query: debounced, results: [], loading: false, error: error instanceof Error ? error.message : 'Search failed' });
      });
    return () => controller.abort();
  }, [debounced]);

  const typing = query.trim() !== debounced && query.trim().length >= MIN_QUERY_LENGTH;
  return typing ? { ...state, loading: true } : state;
}
